import { nextTick, onMounted, watch, type Ref } from "vue";
import { useSharedState } from "composables/useSharedState";
import { useLogger } from "composables/useLogger";
import QRCode from "qrcode";
import type { QRCodeRenderersOptions } from "qrcode";
import type { QrCodeProps } from "./qrcode.types";

export interface UseQrCodeOptions {
  props: Readonly<QrCodeProps>;
  canvasRef: Ref<HTMLCanvasElement | null>;
  svgContainerRef: Ref<HTMLElement | null>;
}

/** Build the options object consumed by the `qrcode` library. */
const buildOptions = (props: Readonly<QrCodeProps>): QRCodeRenderersOptions => {
  const opts: QRCodeRenderersOptions = {
    errorCorrectionLevel: props.errorCorrectionLevel,
    margin: props.margin,
    scale: props.scale,
    width: props.width,
  };
  if (props.version != null) (opts as Record<string, unknown>).version = props.version;
  if (props.maskPattern != null) (opts as Record<string, unknown>).maskPattern = props.maskPattern;
  if (props.colorDark || props.colorLight) {
    opts.color = { dark: props.colorDark ?? "#000000ff", light: props.colorLight ?? "#ffffffff" };
  }
  return opts;
};

type Logger = ReturnType<typeof useLogger>;

const renderSvg = async (
  container: HTMLElement,
  data: string,
  opts: QRCodeRenderersOptions,
  logger: Logger,
): Promise<void> => {
  try {
    const svgString = await QRCode.toString(data, { ...opts, type: "svg" });
    const svgEl = new DOMParser().parseFromString(svgString, "image/svg+xml").documentElement;
    container.textContent = "";
    if (svgEl instanceof SVGElement) container.append(document.importNode(svgEl, true));
  } catch (error) {
    logger.error("Error generating SVG QR code:", error);
    container.textContent = "";
  }
};

const renderCanvas = async (
  canvas: HTMLCanvasElement,
  data: string,
  opts: QRCodeRenderersOptions,
  logger: Logger,
): Promise<void> => {
  try {
    await QRCode.toCanvas(canvas, data, opts);
  } catch (error) {
    logger.error("Error generating canvas QR code:", error);
  }
};

export const useQrCode = (options: UseQrCodeOptions) => {
  const { props, canvasRef, svgContainerRef } = options;
  useSharedState(props);
  const logger = useLogger("QrCode");

  const renderQrCode = async (): Promise<void> => {
    if (!props.data) return;
    const opts = buildOptions(props);
    if (props.type === "svg") {
      const container = svgContainerRef.value;
      if (container) await renderSvg(container, props.data, opts, logger);
      return;
    }
    await nextTick();
    if (canvasRef.value) await renderCanvas(canvasRef.value, props.data, opts, logger);
  };

  onMounted(renderQrCode);
  watch(() => ({ ...props }), renderQrCode, { deep: true });
};
